import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import TrackedLink from "@/components/TrackedLink";
import { ArrowLeft, ArrowRight, Mail, HardDrive, Users, FolderOpen, Globe, Shield, Cloud, Settings, ShieldCheck, Lock, Ban, Eye, RefreshCw } from "lucide-react";
import { motion } from "framer-motion";
import cloudOnlyHeroBg from "@/assets/cloud-only-hero-bg.jpg";

import outlookIcon from "@/assets/ms-icons/outlook.svg";
import onedriveIcon from "@/assets/ms-icons/onedrive.svg";
import teamsIcon from "@/assets/ms-icons/teams.svg";
import sharepointIcon from "@/assets/ms-icons/sharepoint.svg";
import wordIcon from "@/assets/ms-icons/word.svg";
import excelIcon from "@/assets/ms-icons/excel.svg";
import powerpointIcon from "@/assets/ms-icons/powerpoint.svg";
import onenoteIcon from "@/assets/ms-icons/onenote.svg";
import formsIcon from "@/assets/ms-icons/forms.svg";
import plannerIcon from "@/assets/ms-icons/planner.svg";
import bookingsIcon from "@/assets/ms-icons/bookings.svg";
import defenderIcon from "@/assets/ms-icons/defender.svg";

type FeatureItem = {
  icon: string;
  text: string;
};

const features: FeatureItem[] = [
  { icon: outlookIcon, text: "Zakelijke e-mail met Exchange Online (50 GB per mailbox)" },
  { icon: onedriveIcon, text: "1 TB OneDrive-opslag per gebruiker" },
  { icon: teamsIcon, text: "Microsoft Teams voor chatten, bellen en videobellen" },
  { icon: sharepointIcon, text: "SharePoint Online voor documentbeheer en samenwerking" },
  { icon: wordIcon, text: "Word (web- en mobiele versie)" },
  { icon: excelIcon, text: "Excel (web- en mobiele versie)" },
  { icon: powerpointIcon, text: "PowerPoint (web- en mobiele versie)" },
  { icon: onenoteIcon, text: "OneNote (web- en mobiele versie)" },
  { icon: bookingsIcon, text: "Microsoft Bookings voor afsprakenbeheer" },
  { icon: formsIcon, text: "Microsoft Forms" },
  { icon: plannerIcon, text: "Microsoft Planner en Lists" },
  { icon: defenderIcon, text: "Bescherming tegen spam en phishing" },
];

const highlights = [
  {
    icon: Mail,
    title: "Zakelijke e-mail",
    text: "Professioneel mailen met je eigen domeinnaam. 50 GB opslag per mailbox, automatisch gesynchroniseerd op al je apparaten.",
    color: "bg-hero-gradient",
  },
  {
    icon: HardDrive,
    title: "1 TB cloudopslag",
    text: "Al je bestanden veilig in OneDrive. Automatische versiegeschiedenis en eenvoudig delen met collega's of klanten.",
    color: "bg-rinqel-blue-gradient",
  },
  {
    icon: Users,
    title: "Samenwerken in Teams",
    text: "Chatten, videobellen en bestanden delen in één app. Elk team krijgt automatisch een eigen SharePoint-opslagplek.",
    color: "bg-rinqel-green-gradient",
  },
  {
    icon: FolderOpen,
    title: "SharePoint voor je bedrijf",
    text: "Een centrale plek voor documenten, processen en kennis. Maak eenvoudig afdelingssites aan zonder technische kennis.",
    color: "bg-hero-gradient",
  },
  {
    icon: Globe,
    title: "Overal werken",
    text: "Gebruik de webversies van Word, Excel en PowerPoint op elk apparaat. Altijd en overal toegang tot je werk.",
    color: "bg-rinqel-blue-gradient",
  },
  {
    icon: Shield,
    title: "Inclusief back-up",
    text: "Wij maken doorlopend offsite back-ups van je mailbox, OneDrive en SharePoint. Altijd inbegrepen, geen extra kosten.",
    color: "bg-rinqel-green-gradient",
  },
];

const WerkplekManagedCloudOnly = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[420px] overflow-hidden">
        <div className="absolute inset-0">
          <img loading="eager" decoding="async" fetchPriority="high" src={cloudOnlyHeroBg} alt="Cloud Only werkplek: volledig beheerd vanuit de cloud" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[hsl(210,25%,12%)]/90 via-[hsl(210,30%,18%)]/80 to-[hsl(204,60%,30%)]/70" />
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.1),transparent_60%)]" />
        <div className="container relative z-10 flex min-h-[420px] items-center py-20">
          <div className="max-w-2xl">
            <Link
              to="/diensten/werkplek"
              className="mb-6 inline-flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Terug naar Digitale Werkplek
            </Link>

            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Op basis van Microsoft 365 Business Basic
              </span>
            </motion.div>

            <motion.div
              className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.05 }}
            >
              <Cloud className="h-7 w-7 text-white" />
            </motion.div>

            <motion.h1
              className="text-4xl font-bold leading-[1.1] tracking-tight text-white md:text-5xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Managed Cloud Only{" "}
              <span className="text-gradient">Werkplek</span>
            </motion.h1>

            <motion.p
              className="mt-5 max-w-xl text-lg leading-relaxed text-white/80"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Werken vanuit de cloud — zonder lokale Office-apps. Ideaal voor medewerkers die voornamelijk via de browser werken.
            </motion.p>

            <motion.div
              className="mt-8"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <span className="text-4xl font-bold text-white">€25</span>
              <span className="ml-1 text-white/70">/per maand per gebruiker</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Wat zit erin */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Wat zit er allemaal in?
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              De Managed Cloud Only Werkplek geeft je alle essentiële Microsoft 365-tools om samen te werken,
              te mailen en bestanden te delen — volledig beheerd door Rinqel.
            </p>
          </motion.div>

          <div className="mx-auto mt-10 max-w-3xl">
          <div className="rounded-2xl border border-border bg-card p-6 md:p-8 shadow-card">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {features.map((f, i) => (
                  <motion.div
                    key={f.text}
                    className="flex items-center gap-3 rounded-lg border border-border/50 bg-muted/30 px-4 py-3"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: i * 0.04 }}
                  >
                     <img loading="lazy" decoding="async"
                       src={f.icon}
                       alt={f.text.split("(")[0].trim()}
                      width={32}
                      height={32}
                      className="h-8 w-8 shrink-0 object-contain"
                    />
                    <span className="text-sm">{f.text}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="bg-muted/40 py-16 md:py-20">
        <div className="container">
          <motion.h2
            className="mb-12 text-center text-2xl font-bold md:text-3xl"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            De highlights{" "}
            <span className="text-gradient">uitgelicht</span>
          </motion.h2>

          <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-2">
            {highlights.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="flex gap-4 rounded-xl border border-border bg-card p-6 shadow-card transition-shadow hover:shadow-card-hover"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                >
                  <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${item.color} text-primary-foreground`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{item.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Beheer & Beveiliging */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 text-xs font-semibold text-primary">
              <Settings className="h-3.5 w-3.5" />
              Actief beheerd door Rinqel
            </div>
            <h2 className="text-2xl font-bold md:text-3xl">
              Wij houden alles <span className="text-gradient">up-to-date en veilig</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Rinqel beheert je Microsoft-omgeving actief. Wij zorgen dat je tenant altijd up-to-date is,
              beveiligingsinstellingen correct staan en nieuwe functies tijdig worden geactiveerd.
              Jij hoeft je nergens druk om te maken — wij regelen het.
            </p>
          </motion.div>

          <div className="mx-auto mt-10 grid max-w-4xl gap-4 md:grid-cols-2">
            {[
              {
                icon: ShieldCheck,
                title: "Spam- en phishingfilter",
                text: "Verdachte e-mails worden automatisch tegengehouden. Medewerkers krijgen geen gevaarlijke berichten meer in hun inbox.",
                color: "bg-hero-gradient",
              },
              {
                icon: Lock,
                title: "MFA standaard ingeschakeld",
                text: "Elke gebruiker logt in met een extra verificatiestap. Zelfs als een wachtwoord uitlekt, komt niemand binnen.",
                color: "bg-rinqel-blue-gradient",
              },
              {
                icon: Ban,
                title: "Ongeautoriseerde toegang geblokkeerd",
                text: "Verdachte inlogpogingen worden herkend en geblokkeerd. Wij stellen de regels in en houden ze actueel.",
                color: "bg-rinqel-green-gradient",
              },
              {
                icon: Eye,
                title: "Beveiligingsstandaarden actief",
                text: "Microsoft Security Defaults staan aan — een solide basislijn die veelvoorkomende aanvallen voorkomt, zonder extra licentiekosten.",
                color: "bg-hero-gradient",
              },
              {
                icon: RefreshCw,
                title: "Altijd up-to-date",
                text: "Wij monitoren updates, nieuwe functies en beveiligingspatches. Je omgeving draait altijd op de laatste versie.",
                color: "bg-rinqel-blue-gradient",
              },
              {
                icon: Shield,
                title: "Doorlopende back-ups",
                text: "Je mailbox, OneDrive en SharePoint worden continu geback-upt. Mocht er iets misgaan, dan herstellen wij het voor je.",
                color: "bg-rinqel-green-gradient",
              },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="flex gap-4 rounded-xl border border-border bg-card p-6 shadow-card transition-shadow hover:shadow-card-hover"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                >
                  <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${item.color} text-primary-foreground`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{item.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Voor wie */}
      <section className="bg-muted/40 py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Voor wie is dit <span className="text-gradient">geschikt?</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              De Cloud Only Werkplek is ideaal voor medewerkers die geen desktop-versies van Office nodig hebben.
              Denk aan medewerkers die vooral mailen, online documenten bewerken en samenwerken in Teams.
              Alles werkt via de browser — licht, snel en altijd up-to-date.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Verschil met Managed Werkplek */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Verschil met <span className="text-gradient">Managed Werkplek?</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Bij de Cloud Only Werkplek werk je met de webversies van Office. Bij de Managed Werkplek krijg je
              de volledige desktop-apps (Word, Excel, PowerPoint, Outlook), geavanceerde beveiliging met Microsoft Defender,
              apparaatbeheer via Intune én proactief beheer door Rinqel. De complete oplossing voor bedrijven die maximale
              productiviteit en veiligheid willen.
            </p>
            <Button variant="hero-outline" size="lg" className="mt-6" asChild>
              <Link to="/diensten/werkplek/managed">
                Bekijk Managed Werkplek
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-muted/40 py-12">
        <div className="container">
          <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:justify-center">
            <Button variant="hero" size="lg" asChild>
              <TrackedLink to="/contact" trackLabel="Vraag aan" trackLocation="werkplek-cloud-only-cta">
                Vraag aan
                <ArrowRight className="ml-1 h-4 w-4" />
              </TrackedLink>
            </Button>
            <Button variant="hero-outline" size="lg" asChild>
              <Link to="/diensten/werkplek">Terug naar overzicht</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default WerkplekManagedCloudOnly;
